/**
 * Staff Filter Bar Component
 *
 * A filter bar with debounced name search and primary specialty dropdown.
 * Used by the Staff list for server-side filtering.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Button } from '@material-ui/core';

export interface StaffFilter {
  name?: string;
  primarySpecialty?: string;
}

interface StaffFilterBarProps {
  filter?: StaffFilter;
  value?: StaffFilter;
  onChange: (filter: StaffFilter) => void;
}

const PRIMARY_SPECIALTIES = [
  { value: '', label: 'All Specialties' },
  { value: 'PRE_OP', label: 'Pre-Op' },
  { value: 'OR', label: 'OR' },
  { value: 'PACU', label: 'PACU' },
  { value: 'SEDATION', label: 'Sedation' },
];

const StaffFilterBar: React.FC<StaffFilterBarProps> = ({ filter, value, onChange }) => {
  const effectiveFilter = (filter ?? value ?? {}) as StaffFilter;

  // Local state for name input to support debounced updates
  const [nameInput, setNameInput] = useState<string>(effectiveFilter.name || '');
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  // Keep local input in sync when external filter changes (e.g., via URL)
  useEffect(() => {
    setNameInput(effectiveFilter.name || '');
  }, [effectiveFilter.name]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current !== undefined) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    },
    []
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setNameInput(inputValue);

    // Clear any pending update
    if (debounceTimeoutRef.current !== undefined) {
      window.clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce updates to avoid spamming the server while typing
    debounceTimeoutRef.current = window.setTimeout(() => {
      const trimmed = inputValue.trim();
      const nextFilter: StaffFilter = { ...effectiveFilter };

      if (trimmed.length >= 2) {
        nextFilter.name = trimmed;
      } else {
        delete nextFilter.name;
      }

      onChange(nextFilter);
    }, 300);
  };

  const handleSpecialtyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const specialty = event.target.value as string;
    const nextFilter: StaffFilter = { ...effectiveFilter };

    if (specialty) {
      nextFilter.primarySpecialty = specialty;
    } else {
      delete nextFilter.primarySpecialty;
    }

    onChange(nextFilter);
  };

  const handleClearAll = () => {
    setNameInput('');
    onChange({});
  };

  const hasActiveFilters = Boolean(effectiveFilter.name || effectiveFilter.primarySpecialty);

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={16} mb={1}>
      <TextField
        label="Name"
        variant="outlined"
        size="small"
        value={nameInput}
        onChange={handleNameChange}
        placeholder="Search by name..."
        style={{ minWidth: 200 }}
      />
      <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
        <InputLabel id="specialty-filter-label">Primary Specialty</InputLabel>
        <Select
          labelId="specialty-filter-label"
          value={effectiveFilter.primarySpecialty || ''}
          onChange={handleSpecialtyChange}
          label="Primary Specialty"
        >
          {PRIMARY_SPECIALTIES.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasActiveFilters && (
        <Button size="small" onClick={handleClearAll}>
          Clear All
        </Button>
      )}
    </Box>
  );
};

StaffFilterBar.displayName = 'StaffFilterBar';

export default StaffFilterBar;
